import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DealDetails from "./DealDetails";
import { useLocation, useNavigate } from "react-router-dom";
import FilterPage from "../filter/FilterPage";
import UserMenu from "../common/UserMenu";
import { useAuth } from "../../contexts/AuthContext";
import {
  trackEvent,
  trackDealsListView,
  gaFilterAreaParam,
} from "../../utils/analytics";
import { userAPI } from "../../services/api";
import logger from "../../utils/logger";
import { getCurrentLocation } from "../../services/location.service";
import {
  coordinatesToNearestArea,
  resolvedAreaSlugFromNearest,
} from "../../services/area.service";
import DealsMainTabToggle, {
  TAB_DEALS,
  TAB_FOLLOWING,
} from "./DealsMainTabToggle";
import { TabsContext } from "./dealsTabsContext";
import DealsCalendarTab from "./DealsCalendarTab";
import DealsTodayWeekTab from "./DealsTodayWeekTab";
import SavedDealsCalendarTab from "./SavedDealsCalendarTab";
import BrowseRestaurantsPanel from "./BrowseRestaurantsPanel";
import DealsBrowseBottomToggle, {
  DEALS_BROWSE_AVAILABLE,
  DEALS_BROWSE_CALENDAR,
  DEALS_BROWSE_NEXT_WEEK,
  DEALS_BROWSE_THIS_WEEK,
} from "./DealsBrowsePanel/DealsBrowseBottomToggle";
import { useBottomToggleScrollTint } from "../../hooks/useBottomToggleScrollTint";
import { uiNeutrals } from "../../constants/uiNeutrals";
import {
  DEALS_CONTENT_TOP_PADDING_PX,
  DEALS_HEADER_TOP_GAP_PX,
  DEALS_HEADER_ROW_MIN_HEIGHT_PX,
  DEALS_HEADER_BOTTOM_PADDING_PX,
} from "../../constants/dealsShellLayout";
import { pathnameWithSearchPreservingNonFilters } from "../../utils/dealsFilterUrl";
import { isShowingAllDealCategories } from "../../utils/dealCategoryParam";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import { appBackgroundColor } from "../../theme";
import {
  FILTER_PREFERENCES_QUERY_KEY,
  useFilterPreferencesQuery,
} from "../../hooks/useFilterPreferencesQuery";
import ReminderExpiryBanner from "../reminders/ReminderExpiryBanner";
import { useReminderSetupStatus } from "../reminders/hooks/useReminderSetupStatus";
import { useNavigateToReminders } from "../reminders/useNavigateToReminders";
import { registerReminderPromptBlocker } from "../reminders/reminderPromptBlockers";

/** Frosted header chips — no upward box-shadow (clean against status bar) */
const GLASSY_SHADOW = "0 4px 18px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.65)";

const headerFrostedGlassSx = {
  bgcolor: uiNeutrals.frostedFill,
  backdropFilter: "blur(22px) saturate(1.08)",
  WebkitBackdropFilter: "blur(22px) saturate(1.08)",
  boxShadow: GLASSY_SHADOW,
  border: "1px solid rgba(255,255,255,0.55)",
};

// Glass effect lives on the wrapper Box so IconButton click/focus states can't touch it
const glassyButtonWrapperSx = {
  ...headerFrostedGlassSx,
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
};

// The IconButton itself is fully transparent — purely interactive
const glassyIconButtonSx = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  p: 0,
  color: "#000",
  bgcolor: "transparent",
  WebkitTapHighlightColor: "transparent",
  "&&, &&:hover, &&:active, &&:focus, &&:focus-visible, &&.Mui-focusVisible": {
    bgcolor: "transparent",
    boxShadow: "none",
    outline: "none",
  },
};

const DealsTabsContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const navigateToReminders = useNavigateToReminders();

  const [tabValue, setTabValue] = useState(TAB_DEALS);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [openRestaurantDrawer, setOpenRestaurantDrawer] = useState(false);
  const [openSavedDrawer, setOpenSavedDrawer] = useState(false);
  const [dealsSubViewMode, setDealsSubViewMode] = useState(
    DEALS_BROWSE_AVAILABLE,
  );

  const isAvailableSubView =
    dealsSubViewMode === DEALS_BROWSE_AVAILABLE ||
    dealsSubViewMode === DEALS_BROWSE_THIS_WEEK ||
    dealsSubViewMode === DEALS_BROWSE_NEXT_WEEK;
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedDeal, setExpandedDeal] = useState(null);

  const [tempFilters, setTempFilters] = useState({
    timeOfDay: "",
    area: "",
    category: "",
    discountType: "",
    priceBand: "",
  });
  const [tempCurrentArea, setTempCurrentArea] = useState("");
  const [followingFilters, setFollowingFilters] = useState({
    timeOfDay: "",
    date: "",
    area: "",
    category: "",
    discountType: "",
    priceBand: "",
  });

  const dealsScrollRef = useRef(null);
  const followingScrollRef = useRef(null);
  const filterDrawerWasOpenRef = useRef(false);

  const dealsBottomToggleElevated = useBottomToggleScrollTint(
    dealsScrollRef,
    tabValue === TAB_DEALS,
  );

  const followingBottomToggleElevated = useBottomToggleScrollTint(
    followingScrollRef,
    tabValue === TAB_FOLLOWING,
  );

  const contentOffsetPx = DEALS_CONTENT_TOP_PADDING_PX;

  const reminderStatusQueryEnabled =
    tabValue === TAB_DEALS || openSavedDrawer || openFilterDrawer;

  const { showReminderSetupBanner: showSavedDealsReminderBanner } =
    useReminderSetupStatus(reminderStatusQueryEnabled);

  useEffect(() => {
    const overlaysOpen =
      openFilterDrawer ||
      openSavedDrawer ||
      openRestaurantDrawer ||
      Boolean(expandedDeal) ||
      menuOpen;
    if (!overlaysOpen) return undefined;
    return registerReminderPromptBlocker("deals-overlays");
  }, [
    openFilterDrawer,
    openSavedDrawer,
    openRestaurantDrawer,
    expandedDeal,
    menuOpen,
  ]);

  const openReminderSetupFromSavedDrawer = () => {
    setOpenSavedDrawer(false);
    navigateToReminders("saved_drawer");
  };

  const lastDealsListViewKeyRef = useRef(null);

  useEffect(() => {
    if (location.pathname !== "/deals") {
      lastDealsListViewKeyRef.current = null;
      return;
    }

    const viewKey = [
      location.pathname,
      location.search,
      tabValue,
      dealsSubViewMode,
    ].join("|");
    if (lastDealsListViewKeyRef.current === viewKey) return;
    lastDealsListViewKeyRef.current = viewKey;

    const p = new URLSearchParams(location.search);
    trackDealsListView({
      main_tab: tabValue === TAB_FOLLOWING ? "following" : "deals",
      browse_mode: dealsSubViewMode,
      logged_in: isAuthenticated,
      area: gaFilterAreaParam(p.get("area") || ""),
      has_filters: Boolean(
        p.get("area") ||
          p.get("time_of_day") ||
          p.get("category") ||
          p.get("discount_type") ||
          p.get("price_band"),
      ),
    });
  }, [
    location.pathname,
    location.search,
    tabValue,
    dealsSubViewMode,
    isAuthenticated,
  ]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const area = p.get("area") || "";
    const timeOfDay = p.get("time_of_day") || "";
    const category = p.get("category") || "";
    const discountType = p.get("discount_type") || "";
    const priceBand = p.get("price_band") || "";
    if (area || timeOfDay || category || discountType || priceBand) {
      setFollowingFilters({
        timeOfDay,
        date: "",
        area,
        category,
        discountType,
        priceBand,
      });
      return;
    }
    if (!isAuthenticated) {
      setFollowingFilters({
        timeOfDay: "",
        date: "",
        area: "",
        category: "",
        discountType: "",
        priceBand: "",
      });
    }
  }, [location.search, isAuthenticated]);

  const urlHasFilterParams = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return Boolean(
      p.get("area") ||
      p.get("time_of_day") ||
      p.get("category") ||
      p.get("discount_type") ||
      p.get("price_band"),
    );
  }, [location.search]);

  const { data: serverFilterPreferences } =
    useFilterPreferencesQuery(urlHasFilterParams);

  useEffect(() => {
    if (!isAuthenticated || urlHasFilterParams || !serverFilterPreferences) {
      return;
    }
    const fp = serverFilterPreferences;
    setFollowingFilters({
      timeOfDay: fp.timeOfDay || "",
      date: "",
      area: fp.area || "",
      category: fp.category || "",
      discountType: "",
      priceBand: "",
    });
    const savedArea = fp.area || "";
    setTempCurrentArea(savedArea && savedArea !== "current" ? savedArea : "");
  }, [isAuthenticated, urlHasFilterParams, serverFilterPreferences]);

  useEffect(() => {
    const s = location.state;
    if (s?.dealsTab === 1) {
      setTabValue(TAB_FOLLOWING);
    }
    if (s?.openRestaurantDrawer) {
      setOpenRestaurantDrawer(true);
    }
    if (s?.dealsTab !== undefined || s?.openRestaurantDrawer) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (openRestaurantDrawer) {
      setOpenFilterDrawer(false);
      setOpenSavedDrawer(false);
    }
  }, [openRestaurantDrawer]);

  useEffect(() => {
    if (openSavedDrawer) {
      setOpenFilterDrawer(false);
    }
  }, [openSavedDrawer]);

  // Always-mounted listener: update the savedDeals cache the moment a deal is
  // saved or unsaved, even while the saved-deals drawer is closed (and its tab
  // is unmounted due to keepMounted: false).
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleSavedDealsChanged = (e) => {
      const { savedDeal, unsavedShortId } = e?.detail || {};

      if (savedDeal) {
        queryClient.setQueryData(["savedDeals"], (prev) => {
          const list = prev || [];
          if (list.some((d) => d._id === savedDeal._id)) return list;
          return [savedDeal, ...list];
        });
      } else if (unsavedShortId) {
        queryClient.setQueryData(["savedDeals"], (prev) => {
          if (!prev) return prev;
          return prev.filter(
            (d) =>
              d.shortId !== unsavedShortId && d._id !== unsavedShortId,
          );
        });
      }

      // Background sync to validate the optimistic update
      queryClient.invalidateQueries({ queryKey: ["savedDeals"], refetchType: "all" });
    };

    window.addEventListener("savedDealsChanged", handleSavedDealsChanged);
    return () => window.removeEventListener("savedDealsChanged", handleSavedDealsChanged);
  }, [isAuthenticated, queryClient]);

  useEffect(() => {
    if (menuOpen) {
      setOpenRestaurantDrawer(false);
      setOpenSavedDrawer(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      window.dispatchEvent(new Event("closeDealDetails"));
      setExpandedDeal(null);
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleCloseDealDetails = () => {
      setExpandedDeal(null);
    };
    window.addEventListener("closeDealDetails", handleCloseDealDetails);
    return () => {
      window.removeEventListener("closeDealDetails", handleCloseDealDetails);
    };
  }, []);

  const handleExpandDeal = (deal) => {
    setExpandedDeal(deal);
  };

  const handleCloseExpandedDeal = () => {
    setExpandedDeal(null);
  };

  const navLinks = [
    { to: "/home", label: "Home" },
    { to: "/deals", label: "Browse" },
    ...(isAuthenticated ? [] : [{ to: "/login", label: "Login" }]),
  ];

  useEffect(() => {
    const handleCurrentLocationUpdate = (event) => {
      const { area, currentArea } = event.detail;
      setTempFilters((prev) => ({ ...prev, area }));
      setTempCurrentArea(currentArea || "");
    };

    window.addEventListener(
      "currentLocationUpdated",
      handleCurrentLocationUpdate,
    );
    return () => {
      window.removeEventListener(
        "currentLocationUpdated",
        handleCurrentLocationUpdate,
      );
    };
  }, []);

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  const handleApplyFilters = useCallback(async () => {
    const parentFilters = tempFilters;
    const currentArea = tempCurrentArea;
    const appliedArea =
      parentFilters.area === "current" ? currentArea : parentFilters.area || "";
    const appliedTimeOfDay = parentFilters.timeOfDay || "";
    const appliedCategory = isShowingAllDealCategories(
      parentFilters.category || "",
    )
      ? ""
      : parentFilters.category || "";

    trackEvent("filter_applied", {
      time_of_day: appliedTimeOfDay,
      date_filter: "",
      area: gaFilterAreaParam(appliedArea),
      category_filter: appliedCategory,
    });

    if (isAuthenticated) {
      try {
        await userAPI.updateFilterPreferences({
          timeOfDay: appliedTimeOfDay,
          date: "",
          area: appliedArea,
          category: appliedCategory,
        });
        queryClient.setQueryData(FILTER_PREFERENCES_QUERY_KEY, {
          timeOfDay: appliedTimeOfDay,
          date: "",
          area: appliedArea,
          category: appliedCategory,
        });
      } catch (error) {
        logger.error("Failed to save filter preferences:", error);
      }
    }

    setFollowingFilters({
      timeOfDay: appliedTimeOfDay,
      date: "",
      area: appliedArea,
      category: appliedCategory,
      discountType: parentFilters.discountType || "",
      priceBand: parentFilters.priceBand || "",
    });

    if (!appliedArea) {
      try {
        localStorage.removeItem("selectedLocation");
      } catch {
        // ignore
      }
    } else {
      try {
        localStorage.setItem(
          "selectedLocation",
          JSON.stringify({
            name: appliedArea,
            area: appliedArea,
            coordinates: null,
            category: appliedCategory,
          }),
        );
      } catch {
        // ignore
      }
    }

    navigate(
      pathnameWithSearchPreservingNonFilters(
        location.pathname,
        location.search,
      ),
    );
    setOpenFilterDrawer(false);
  }, [
    tempFilters,
    tempCurrentArea,
    isAuthenticated,
    location.pathname,
    location.search,
    navigate,
    queryClient,
  ]);

  const handleGetCurrentLocation = useCallback(async () => {
    try {
      const coords = await getCurrentLocation();
      const nearestArea = coordinatesToNearestArea(
        coords.latitude,
        coords.longitude,
      );
      if (!nearestArea) {
        window.alert(
          "Unable to determine your location. Please select a location manually.",
        );
        return;
      }
      const areaSlug = resolvedAreaSlugFromNearest(nearestArea);
      const locationData = {
        name: areaSlug,
        area: areaSlug,
        coordinates: coords,
        category: tempFilters.category || "",
      };
      try {
        localStorage.setItem("selectedLocation", JSON.stringify(locationData));
      } catch {
        // ignore
      }
      setTempFilters((prev) => ({ ...prev, area: "current" }));
      setTempCurrentArea(areaSlug);
      setFollowingFilters((prev) => ({
        ...prev,
        area: areaSlug,
        category: tempFilters.category || prev.category || "",
      }));
      window.dispatchEvent(
        new CustomEvent("currentLocationUpdated", {
          detail: { area: "current", currentArea: areaSlug },
        }),
      );
      navigate(
        pathnameWithSearchPreservingNonFilters(
          location.pathname,
          location.search,
        ),
      );
    } catch (error) {
      logger.error("Location error:", error);
      window.alert(
        "Unable to get your current location. Please try again or select a location manually.",
      );
    }
  }, [location.pathname, location.search, navigate, tempFilters.category]);

  useEffect(() => {
    if (openFilterDrawer && !filterDrawerWasOpenRef.current) {
      const p = new URLSearchParams(location.search);
      const areaParam = p.get("area") || followingFilters.area || "";
      setTempFilters({
        timeOfDay: p.get("time_of_day") || followingFilters.timeOfDay || "",
        area: areaParam,
        category: p.get("category") || followingFilters.category || "",
        discountType:
          p.get("discount_type") || followingFilters.discountType || "",
        priceBand: p.get("price_band") || followingFilters.priceBand || "",
      });
      setTempCurrentArea(areaParam);
    }
    filterDrawerWasOpenRef.current = openFilterDrawer;
  }, [openFilterDrawer, location.search, followingFilters]);

  const tabsContextValue = useMemo(
    () => ({
      renderSearchInHeader: false,
      openFilterDrawer,
      setOpenFilterDrawer,
      syncFiltersToParent: () => {},
      dealsScrollRef: null,
      isActive: false,
      followingFilters,
    }),
    [openFilterDrawer, followingFilters],
  );

  return (
    <TabsContext.Provider value={tabsContextValue}>
      <Helmet>
        <title>Explore Food Deals in Malaysia — Loops</title>
        <meta name="description" content="Discover the best food deals near you. Browse discounted breakfast, lunch, and dinner deals at restaurants across Malaysia." />
        <meta property="og:title" content="Explore Food Deals in Malaysia — Loops" />
        <meta property="og:description" content="Discover the best food deals near you. Browse discounted breakfast, lunch, and dinner deals at restaurants across Malaysia." />
        <meta property="og:url" content="https://loopsfood.com/deals" />
        <link rel="canonical" href="https://loopsfood.com/deals" />
      </Helmet>
      <UserMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        navLinks={navLinks}
      />

      <FilterPage
        isOpen={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
        tempFilters={tempFilters}
        tempCurrentArea={tempCurrentArea}
        onTimeOfDayChange={(value) =>
          setTempFilters((prev) => ({ ...prev, timeOfDay: value }))
        }
        onAreaChange={(value) => {
          setTempFilters((prev) => ({ ...prev, area: value }));
          setTempCurrentArea("");
        }}
        onCategoryChange={(nextCategory) => {
          setTempFilters((prev) => ({
            ...prev,
            category: nextCategory || "",
          }));
        }}
        onDiscountFilterChange={({ discountType, priceBand }) => {
          setTempFilters((prev) => ({
            ...prev,
            discountType: discountType || "",
            priceBand: priceBand || "",
          }));
        }}
        onGetCurrentLocation={handleGetCurrentLocation}
        onApply={handleApplyFilters}
      />

      <Drawer
        anchor="bottom"
        open={openRestaurantDrawer}
        onClose={() => setOpenRestaurantDrawer(false)}
        sx={{ zIndex: 10100 }}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          elevation: 0,
          sx: {
            width: "100%",
            maxWidth: "100%",
            height: "92dvh",
            maxHeight: "92dvh",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: appBackgroundColor,
            borderTop: uiNeutrals.frostedToggleBorder,
            boxShadow: "0 -10px 36px rgba(0, 0, 0, 0.1)",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(5px) saturate(1.05)",
            WebkitBackdropFilter: "blur(5px) saturate(1.05)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            pt: 1.5,
            pb: 1.25,
            flexShrink: 0,
            bgcolor: "transparent",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#000",
              minWidth: 0,
            }}
          >
            Restaurants
          </Typography>
          <IconButton
            onClick={() => setOpenRestaurantDrawer(false)}
            edge="end"
            aria-label="Close restaurants"
            sx={closeIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
            overscrollBehavior: "contain",
            pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <BrowseRestaurantsPanel
            isActive={openRestaurantDrawer}
            deferFollowPersistenceUntilClose
          />
        </Box>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={openSavedDrawer}
        onClose={() => setOpenSavedDrawer(false)}
        sx={{ zIndex: 10100 }}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          elevation: 0,
          sx: {
            width: "100%",
            maxWidth: "100%",
            height: "92dvh",
            maxHeight: "92dvh",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: appBackgroundColor,
            borderTop: uiNeutrals.frostedToggleBorder,
            boxShadow: "0 -10px 36px rgba(0, 0, 0, 0.1)",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(5px) saturate(1.05)",
            WebkitBackdropFilter: "blur(5px) saturate(1.05)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            pt: 1.5,
            pb: 1.25,
            flexShrink: 0,
            bgcolor: "transparent",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#000",
              minWidth: 0,
            }}
          >
            Saved deals
          </Typography>
          <IconButton
            onClick={() => setOpenSavedDrawer(false)}
            edge="end"
            aria-label="Close saved deals"
            sx={closeIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
            overscrollBehavior: "contain",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <SavedDealsCalendarTab
            isActive={openSavedDrawer}
            onExpandDeal={handleExpandDeal}
          />
        </Box>
        {showSavedDealsReminderBanner ? (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 1.25,
              pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
              bgcolor: appBackgroundColor,
            }}
          >
            <ReminderExpiryBanner onSetUp={openReminderSetupFromSavedDrawer} />
          </Box>
        ) : null}
      </Drawer>

      <Box
        sx={{
          width: "100%",
          position: "relative",
          WebkitTapHighlightColor: "transparent !important",
          "& *": {
            WebkitTapHighlightColor: "transparent !important",
            userSelect: "none !important",
            WebkitTouchCallout: "none !important",
            WebkitUserSelect: "none !important",
          },
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            pt: `calc(env(safe-area-inset-top, 0px) + ${DEALS_HEADER_TOP_GAP_PX}px)`,
            pb: `${DEALS_HEADER_BOTTOM_PADDING_PX}px`,
            zIndex: 999,
            bgcolor: "transparent",
            overflow: "visible",
            "&::-webkit-scrollbar": {
              display: "none",
              width: 0,
              height: 0,
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
              backdropFilter: "blur(20px) saturate(1.08)",
              WebkitBackdropFilter: "blur(20px) saturate(1.08)",
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 38%, rgba(0,0,0,0.18) 68%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 38%, rgba(0,0,0,0.18) 68%, rgba(0,0,0,0) 100%)",
            }}
          />
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              minHeight: `${DEALS_HEADER_ROW_MIN_HEIGHT_PX}px`,
              px: 2,
            }}
          >
            {/* Same width as right so the tab pill stays visually centered */}
            <Box
              sx={{
                width: 40,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Box sx={glassyButtonWrapperSx}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL || ""}/assets/menu.png`}
                  alt=""
                  sx={{ width: 24, height: 24, display: "block", objectFit: "contain", pointerEvents: "none" }}
                />
                <IconButton
                  onClick={() => setMenuOpen(true)}
                  aria-label="Menu"
                  sx={glassyIconButtonSx}
                />
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DealsMainTabToggle
                tabValue={tabValue}
                onTabChange={handleTabChange}
              />
            </Box>

            <Box
              sx={{
                width: 40,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {tabValue === TAB_DEALS && (
                <Box sx={glassyButtonWrapperSx}>
                  <BookmarkBorderOutlinedIcon sx={{ fontSize: 22, pointerEvents: "none" }} />
                  <IconButton
                    onClick={() => setOpenSavedDrawer(true)}
                    aria-label="Saved deals"
                    sx={glassyIconButtonSx}
                  />
                </Box>
              )}
              {tabValue === TAB_FOLLOWING && (
                <Box sx={glassyButtonWrapperSx}>
                  <StorefrontOutlinedIcon sx={{ fontSize: 22, pointerEvents: "none" }} />
                  <IconButton
                    onClick={() => setOpenRestaurantDrawer(true)}
                    aria-label="Browse restaurants you follow"
                    sx={glassyIconButtonSx}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            position: "relative",
            mt: 0,
            height: "100vh",
            WebkitTapHighlightColor: "transparent !important",
            "& *": {
              WebkitTapHighlightColor: "transparent !important",
            },
          }}
        >
          <Box
            ref={dealsScrollRef}
            data-reminder-scroll-root
            sx={{
              width: "100%",
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "100%",
              pt: `calc(env(safe-area-inset-top, 0px) + ${contentOffsetPx}px)`,
              display: tabValue === TAB_DEALS ? "block" : "none",
              WebkitTapHighlightColor: "transparent",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <Box
              sx={{ position: "relative", width: "100%", minHeight: "100%" }}
            >
              <Box
                sx={{
                  display:
                    dealsSubViewMode === DEALS_BROWSE_CALENDAR
                      ? "block"
                      : "none",
                }}
              >
                <DealsCalendarTab
                  isActive={
                    tabValue === TAB_DEALS &&
                    dealsSubViewMode === DEALS_BROWSE_CALENDAR
                  }
                  onExpandDeal={handleExpandDeal}
                />
              </Box>
              <Box
                sx={{
                  display: isAvailableSubView ? "block" : "none",
                }}
              >
                <DealsTodayWeekTab
                  isActive={tabValue === TAB_DEALS && isAvailableSubView}
                  onExpandDeal={handleExpandDeal}
                />
              </Box>
              <DealsBrowseBottomToggle
                mode={dealsSubViewMode}
                onModeChange={setDealsSubViewMode}
                elevated={dealsBottomToggleElevated}
              />
            </Box>
          </Box>

          <Box
            ref={followingScrollRef}
            data-reminder-scroll-root
            sx={{
              width: "100%",
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "100%",
              pt: `calc(env(safe-area-inset-top, 0px) + ${contentOffsetPx}px)`,
              display: tabValue === TAB_FOLLOWING ? "block" : "none",
              WebkitTapHighlightColor: "transparent",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <Box
              sx={{ position: "relative", width: "100%", minHeight: "100%" }}
            >
              <Box
                sx={{
                  display:
                    dealsSubViewMode === DEALS_BROWSE_CALENDAR
                      ? "block"
                      : "none",
                }}
              >
                <DealsCalendarTab
                  isActive={
                    tabValue === TAB_FOLLOWING &&
                    dealsSubViewMode === DEALS_BROWSE_CALENDAR
                  }
                  onExpandDeal={handleExpandDeal}
                  followingOnly
                />
              </Box>
              <Box
                sx={{
                  display: isAvailableSubView ? "block" : "none",
                }}
              >
                <DealsTodayWeekTab
                  isActive={tabValue === TAB_FOLLOWING && isAvailableSubView}
                  onExpandDeal={handleExpandDeal}
                  followingOnly
                />
              </Box>
              <DealsBrowseBottomToggle
                mode={dealsSubViewMode}
                onModeChange={setDealsSubViewMode}
                elevated={followingBottomToggleElevated}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <DealDetails
        deal={expandedDeal}
        isOpen={Boolean(expandedDeal)}
        onClose={handleCloseExpandedDeal}
      />
    </TabsContext.Provider>
  );
};

export default DealsTabsContainer;
